import { CURRENT_NETWORK } from "./networks";

// Storage contract address — this is where users approve USDT and where state lives
export const STORAGE_ADDRESS = CURRENT_NETWORK.storageAddress;
export const LOGIC_ADDRESS   = CURRENT_NETWORK.logicAddress;
export const MIGRATOR_ADDRESS = CURRENT_NETWORK.migratorAddress;
export const USDT_ADDRESS = CURRENT_NETWORK.usdtAddress;

// Legacy alias — CONTRACT_ADDRESS still points to Storage for backward-compat imports.
// New code should use STORAGE_ADDRESS.
export const CONTRACT_ADDRESS = STORAGE_ADDRESS;

// ============================================================
// Every event declared in ANY of the three contracts —
// put in one array so both STORAGE_ABI and LOGIC_ABI can include them.
// This lets ethers auto-decode logs from tx receipts on either contract.
// ============================================================
const SHARED_EVENTS = [
  "event UserRegistered(uint256 indexed userId, address indexed wallet, uint256 indexed referrerId, uint256 serialNumber, bytes32 name, uint8 registrationType)",
  "event PackageActivated(uint256 indexed userId, uint256 indexed packageId, uint8 activationType)",
  "event PackageRetoppedUp(uint256 indexed userId, uint256 indexed packageId, uint256 amount, uint256 newCeiling)",
  "event DirectReferralIncome(uint256 indexed fromId, uint256 indexed toId, uint256 amount, uint256 packageId)",
  "event MatrixLevelIncome(uint256 indexed fromId, uint256 indexed toId, uint256 amount, uint256 level, uint256 packageId)",
  "event MatrixPlacement(uint256 indexed userId, uint256 indexed uplineId, uint256 packageId)",
  "event LevelCompleted(uint256 indexed userId, uint256 indexed packageId, uint256 level)",
  "event DailyClubClaimed(uint256 indexed userId, uint256 indexed clubId, uint256 amount)",
  "event DailyEligibilityUpdated(uint256 indexed userId, uint256 indexed clubId, bool eligible)",
  "event DailyPoolDistributed(uint256 indexed clubId, uint256 amount, uint256 eligibleCount)",
  "event CeilingUpgraded(uint256 indexed userId, uint256 indexed packageId, uint256 newCeiling)",
  "event CeilingHit(uint256 indexed userId, uint256 indexed packageId, uint256 allowedAmount, uint256 excessAmount)",
  "event MissingIncome(uint256 amount)",
  "event UserBlocked(uint256 indexed userId)",
  "event UserUnblocked(uint256 indexed userId)",
  "event NameUpdated(uint256 indexed userId, bytes32 newName)",
  "event WalletUpdated(uint256 indexed userId, address indexed oldWallet, address indexed newWallet)",
  "event AdminChanged(address indexed oldAdmin, address indexed newAdmin)",
  "event OwnerChanged(address indexed oldOwner, address indexed newOwner)",
  "event TeamWalletUpdated(address indexed newTeamWallet)",
  "event MissingIncomeWalletUpdated(address indexed newWallet)",
  "event EmergencyWithdraw(uint256 amount)",
  "event MigratorChanged(address indexed oldMigrator, address indexed newMigrator)",
  "event MigrationLocked()",
  "event MigrationUserImported(uint256 indexed userId, address wallet, uint256 sponsorId)",
  "event MigrationPackageActivated(uint256 indexed userId, uint256 indexed packageId)",
  "event MigrationPackageEarningsSet(uint256 indexed userId, uint256 indexed packageId, uint256 totalInvested, uint256 totalEarned, uint256 ceilingLimit, uint8 ceilingTier)",
  "event MigrationUserTotalsSet(uint256 indexed userId, uint256 totalIncome, uint256 totalClaimed)",
  "event MigrationActiveDirectsSet(uint256 indexed userId, uint256 indexed packageId, uint256 count)",
  "event MigrationCreditSet(uint256 indexed userId, uint8 poolType, uint256 clubId, uint256 amount)",
  "event MigrationTotalInvestmentSet(uint256 oldTotal, uint256 newTotal)",
];

// ============================================================
// STORAGE ABI — reads, admin state writes, migration
// ============================================================
export const STORAGE_ABI = [
  // -------- User views --------
  "function addressToId(address) external view returns (uint256)",
  "function users(uint256) external view returns (address wallet, bytes32 name, uint256 id, uint256 referrerId, uint256 serialNumber, uint256 joinTime, bool isBlocked, uint8 registrationType, uint256 totalDirectReferrals, uint256 totalClaimed, uint256 totalIncome)",
  "function getUser(uint256 userId) external view returns (tuple(address wallet, bytes32 name, uint256 id, uint256 referrerId, uint256 serialNumber, uint256 joinTime, bool isBlocked, uint8 registrationType, uint256 totalDirectReferrals, uint256 totalClaimed, uint256 totalIncome))",
  "function userWallet(uint256 userId) external view returns (address)",
  "function userBlocked(uint256 userId) external view returns (bool)",
  "function userReferrer(uint256 userId) external view returns (uint256)",
  "function userExists(uint256 userId) external view returns (bool)",
  "function userPackages(uint256 userId, uint256 packageId) external view returns (bool isActive, uint8 ceilingTier, uint256 activationTime, uint256 totalInvested, uint256 totalEarned, uint256 ceilingLimit)",
  "function getPackage(uint256 userId, uint256 packageId) external view returns (tuple(bool isActive, uint8 ceilingTier, uint256 activationTime, uint256 totalInvested, uint256 totalEarned, uint256 ceilingLimit))",
  "function packageActive(uint256 userId, uint256 packageId) external view returns (bool)",
  "function userIncomeBreakdown(uint256) external view returns (uint256 directReferralIncome, uint256 matrixLevelIncome, uint256 dailyClubIncome)",
  "function activeDirectsPerPackage(uint256 userId, uint256 packageId) external view returns (uint256)",
  "function getUserReferrals(uint256 userId) external view returns (uint256[])",
  "function getUserEligibility(uint256 userId) external view returns (bool[7] royaltyEligible)",
  "function getUserClaimableRaw(uint256 userId) external view returns (uint256[7] royaltyClaimable, uint256 totalClaimable)",
  "function getMatrixLevel(uint256 userId, uint256 packageId, uint256 level) external view returns (uint256[])",
  "function getMatrixDownlinesArr(uint256 packageId, uint256 userId) external view returns (uint256[])",
  "function getMatrixLevelMembersArr(uint256 packageId, uint256 userId, uint256 level) external view returns (uint256[])",
  "function getUserActivities(uint256 userId, uint256 offset, uint256 limit) external view returns (tuple(uint8 activityType, uint40 timestamp, uint80 relatedId, uint128 amount, uint128 netAmount)[] activities, uint256 storedCount, uint256 totalCount)",
  "function getAdminActivities(address adminAddr, uint256 offset, uint256 limit) external view returns (tuple(uint8 activityType, uint40 timestamp, uint80 relatedId, uint128 amount, uint128 netAmount)[] activities, uint256 storedCount, uint256 totalCount)",
  "function userActivityCount(uint256) external view returns (uint256)",
  "function userActivityPointer(uint256) external view returns (uint256)",
  "function adminActivityCount(address) external view returns (uint256)",
  "function adminActivityPointer(address) external view returns (uint256)",
  "function packageMatrixTeamSize(uint256 packageId, uint256 userId) external view returns (uint256)",
  "function packageMatrixDownline(uint256 packageId, uint256 userId, uint256 index) external view returns (uint256)",
  "function packageMatrixUpline(uint256 packageId, uint256 userId) external view returns (uint256)",
  "function levelCompleted(uint256 packageId, uint256 userId, uint256 level) external view returns (bool)",
  "function totalUsers() external view returns (uint256)",
  "function packagePrices(uint256) external view returns (uint256)",
  "function serialToUserId(uint256) external view returns (uint256)",
  "function nextSerialNumber() external view returns (uint256)",
  "function lastGeneratedId() external view returns (uint256)",
  "function getUsersBySerialRange(uint256 fromSerial, uint256 toSerial) external view returns (uint256[])",

  // -------- Roles / config --------
  "function owner() external view returns (address)",
  "function admin() external view returns (address)",
  "function teamWallet() external view returns (address)",
  "function missingIncomeWallet() external view returns (address)",
  "function logicContract() external view returns (address)",
  "function migrator() external view returns (address)",
  "function migrationLocked() external view returns (bool)",
  "function usdt() external view returns (address)",
  "function DEFAULT_REFERRER_ID() external view returns (uint256)",
  "function MAX_ACTIVITIES() external view returns (uint256)",
  "function MATRIX_LEVELS() external view returns (uint256)",
  "function ACC_PRECISION() external view returns (uint256)",

  // -------- Global stats --------
  "function getStats() external view returns (uint256 totalUsers_, uint256 totalInvestment_, uint256 todayUsers, uint256 todayInvestment)",
  "function totalInvestment() external view returns (uint256)",
  "function dailyUserCount(uint256) external view returns (uint256)",
  "function dailyInvestment(uint256) external view returns (uint256)",

  // -------- Royalty / eligibility state --------
  "function dailyPoolEligibleCount(uint256) external view returns (uint256)",
  "function dailyClubEligibility(uint256 userId, uint256 clubId) external view returns (bool)",
  "function dailyAccIndex(uint256) external view returns (uint256)",
  "function dailyLifetimeDistributed(uint256) external view returns (uint256)",
  "function dailyMissedToWallet(uint256) external view returns (uint256)",
  "function dailyCredit(uint256 userId, uint256 clubId) external view returns (uint256)",
  "function userDailySnapshot(uint256 userId, uint256 clubId) external view returns (uint256)",
  "function getDailyEligibleList(uint256 clubId, uint256 offset, uint256 limit) external view returns (uint256[] list, uint256 total)",

  // -------- Admin state writes (direct to Storage) --------
  "function updateUserName(uint256 userId, bytes32 newName) external",
  "function updateUserWallet(uint256 userId, address newWallet) external",
  "function updateAdmin(address newAdmin) external",
  "function updateTeamWallet(address newWallet) external",
  "function updateMissingIncomeWallet(address newWallet) external",
  "function transferOwnership(address newOwner) external",
  "function emergencyWithdraw(uint256 amount) external",

  // -------- Wiring (owner only) --------
  "function setLogicContract(address _logic) external",
  "function setMigrator(address _migrator) external",
  "function lockMigration() external",

  // -------- Migration entry points (owner/admin/migrator) --------
  "function migrateRegisterUsers(uint256[] userIds, address[] wallets, bytes32[] names, uint256[] sponsorIds) external",
  "function migrateSetPackageEarnings(uint256[] userIds, uint256[] packageIds, uint256[] totalInvested, uint256[] totalEarned, uint256[] ceilingLimits, uint8[] ceilingTiers) external",
  "function migrateSetUserTotals(uint256[] userIds, uint256[] totalIncome, uint256[] totalClaimed, uint256[] directIncome, uint256[] matrixIncome, uint256[] dailyIncome) external",
  "function migrateSetActiveDirectsPerPackage(uint256[] userIds, uint256[] packageIds, uint256[] counts) external",
  "function migrateSetDailyCredit(uint256[] userIds, uint256[] clubIds, uint256[] amounts) external",
  "function migrateSetTotalInvestment(uint256 newTotal) external",

  // -------- Events (shared with Logic) --------
  ...SHARED_EVENTS,
];

// ============================================================
// LOGIC ABI — business logic writes (user + admin)
// ============================================================
export const LOGIC_ABI = [
  // -------- User writes --------
  "function register(uint256 referrerId, bytes32 name) external",
  "function registerFor(address wallet, uint256 referrerId, bytes32 name) external",
  "function activatePackage(uint256 packageId) external",
  "function activatePackageFor(address targetWallet, uint256 packageId) external",
  "function retopupPackage(uint256 packageId) external",
  "function retopupPackageFor(address targetWallet, uint256 packageId) external",
  "function claimDailyClub(uint256 clubId) external",

  // -------- Admin business writes --------
  "function adminRegister(address wallet, uint256 referrerId, bytes32 name, uint256 upToPackageId) external",
  "function adminActivatePackage(uint256 userId, uint256 packageId) external",
  "function adminRetopupPackage(uint256 userId, uint256 packageId) external",
  "function blockUser(uint256 userId) external",
  "function unblockUser(uint256 userId) external",

  // -------- Migration entry (called via Migrator) --------
  "function migrateActivatePackages(uint256[] userIds, uint256[] packageIds) external",

  // -------- View to check wiring --------
  "function s() external view returns (address)", // returns paired Storage address

  // -------- Events (same signatures as Storage — Logic emits these) --------
  ...SHARED_EVENTS,
];

// ============================================================
// MIGRATOR ABI — orchestrates the batch import between contracts
// ============================================================
export const MIGRATOR_ABI = [
  "function stor() external view returns (address)",
  "function logic() external view returns (address)",
  "function owner() external view returns (address)",
  "function admin() external view returns (address)",
  "function completed() external view returns (bool)",
  "function isAuthorized() external view returns (bool)",
  "function targetSnapshot() external view returns (uint256 totalUsers_, uint256 totalInvestment_)",
  "function progress() external view returns (uint256 registerBatches, uint256 usersReg, uint256 activateBatches, uint256 pkgsAct, uint256 earningsBatches, uint256 totalsBatches, uint256 directsBatches, uint256 dailyCreditBatches, uint256 totalInvestmentBatches, bool migratorCompleted, bool targetLocked, address currentMigratorOnTarget)",

  // Forwarders
  "function migrateRegisterUsers(uint256[] userIds, address[] wallets, bytes32[] names, uint256[] sponsorIds) external",
  "function migrateActivatePackages(uint256[] userIds, uint256[] packageIds) external",
  "function migrateSetPackageEarnings(uint256[] userIds, uint256[] packageIds, uint256[] totalInvested, uint256[] totalEarned, uint256[] ceilingLimits, uint8[] ceilingTiers) external",
  "function migrateSetUserTotals(uint256[] userIds, uint256[] totalIncome, uint256[] totalClaimed, uint256[] directIncome, uint256[] matrixIncome, uint256[] dailyIncome) external",
  "function migrateSetActiveDirectsPerPackage(uint256[] userIds, uint256[] packageIds, uint256[] counts) external",
  "function migrateSetDailyCredit(uint256[] userIds, uint256[] clubIds, uint256[] amounts) external",
  "function migrateSetTotalInvestment(uint256 newTotal) external",
  "function markCompleted() external",
  "function transferOwnership(address newOwner) external",
  "function updateAdmin(address newAdmin) external",

  // Events
  "event MigratorSetup(address indexed stor, address indexed logic, address indexed admin)",
  "event BatchExecuted(string indexed kind, uint256 count, uint256 batchNumber)",
  "event MigrationCompleted(uint256 totalUsersRegistered, uint256 totalPackagesActivated)",
  "event OwnershipTransferred(address indexed oldOwner, address indexed newOwner)",
  "event AdminChanged(address indexed oldAdmin, address indexed newAdmin)",
];

// Legacy exports for backward-compat with older imports
export const CONTRACT_ABI = STORAGE_ABI;

export const USDT_ABI = [
  "function balanceOf(address) external view returns (uint256)",
  "function allowance(address, address) external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function transfer(address to, uint256 amount) external returns (bool)",
  "function decimals() external view returns (uint8)",
  "function name() external view returns (string)",
  "function symbol() external view returns (string)",
];
